/**
 * A number typed into a form field, as in a spreadsheet: plain ("5.5",
 * "1,200") or a formula starting with "=" ("=5.5*100", "=(1200+800)/2",
 * "=5.5x100").  Only numbers, + - * / (also x, × and ÷) and brackets are
 * understood — nothing is ever executed as code.
 *
 * Returns null for an empty field and NaN for one that can't be read, so a
 * form can tell "left blank" from "typed something wrong".
 */
export function readNumber(text: string): number | null {
  const s = text.trim();
  if (!s) return null;
  if (s.startsWith('=')) return evalArithmetic(s.slice(1));
  const n = Number(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

/** True when the field holds a formula rather than a plain number. */
export function isFormula(text: string): boolean {
  return text.trim().startsWith('=');
}

function evalArithmetic(src: string): number {
  const tokens = src.replace(/,/g, '').match(/\d+(?:\.\d+)?|\.\d+|\S/g) ?? [];
  if (!tokens.length) return NaN;
  let i = 0;
  const peek = () => tokens[i];
  const take = () => tokens[i++];
  const isMul = (t?: string) => t === '*' || t === 'x' || t === 'X' || t === '×';
  const isDiv = (t?: string) => t === '/' || t === '÷';

  function expr(): number {
    let v = term();
    while (peek() === '+' || peek() === '-') {
      const op = take();
      const r = term();
      v = op === '+' ? v + r : v - r;
    }
    return v;
  }
  function term(): number {
    let v = factor();
    while (isMul(peek()) || isDiv(peek())) {
      const op = take();
      const r = factor();
      v = isDiv(op) ? v / r : v * r;
    }
    return v;
  }
  function factor(): number {
    const t = take();
    if (t === '-') return -factor();
    if (t === '+') return factor();
    if (t === '(') {
      const v = expr();
      return take() === ')' ? v : NaN;
    }
    if (t != null && /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(t)) return Number(t);
    return NaN;
  }

  const v = expr();
  return i === tokens.length && Number.isFinite(v) ? v : NaN;
}
